/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import * as Buttons from '../forms/elements/buttons';
import { Column, Row } from '../layout/flexbox';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import webCamImg from '../assets/images/webcam.png';
import Cookies from 'js-cookie';
import { CAMERA_SETTINGS } from '../core/cookies/cookieKeys';
import WebCam from './WebCam';
import { canEnumerateCameras } from './module';
import {
  IMAGE_QUALITY,
  INPUT_HEIGHT,
  INPUT_WIDTH,
  PREVIEW_HEIGHT,
  PREVIEW_WIDTH,
  SCREENSHOT_FORMAT,
} from './constants';
import { COOL_GREY, DARK_GREY } from '../styles/colors';
import { IImageProps } from '../guest/guestPhoto/IImageProps';
import { isNilOrEmpty, assoc } from '../language/module';
import { Select } from '../forms/elements/select/Select';
import { Option } from '../forms/elements/select/Option';
import { LAYER } from '../layout/zIndex';
import { MediumText, UNSELECTABLE_TEXT } from '../styles/styles';
import { Loader } from '../forms/elements/loader/Loader';
import { FADE_IN_KEYFRAMES } from '../styles/animations';
import { getResource } from '../language/resources/getResource';
import { getJsonCookie } from '..';


// Animations
const FadeDiv = styled('div')`animation: 1s ${FADE_IN_KEYFRAMES};`;
const FadeImg = styled('img')`
  width: ${(props: IImageProps) => props.width};
  height: ${(props: IImageProps) => props.height};
  background-color: ${(props: IImageProps) => props.backgroundColor};
  animation: 1s ${FADE_IN_KEYFRAMES};
`;

const FaceGuide = styled('div')`
  z-index: ${LAYER.ONE.FACE_GUIDE};
  position: absolute;
  left: 50%;
  top: 50%;
  width: 300px;
  height: 300px;
  margin-left: -150px;
  margin-top: -150px;
  border-radius: 100%;
  border: 3px dashed ${DARK_GREY};
  pointer-events: none; /* IE click support to accept Flash permissions */
`;

const ButtonNeutral = styled(Buttons.BigNeutral)`
  margin: 10px;
` as any;

const ButtonPositive = styled(Buttons.BigPositive)`
  margin: 10px;
` as any;

const ButtonNegative = styled(Buttons.BigNegative)`
  margin: 10px;
` as any;

const StyledDiv = styled('div')`
  option {
    ${MediumText};
  }
`;

const CameraLens = css`
  width: 480px; /* Here for IE's lame inability to use sizes other than 320x240 */
  height: 480px;
`;


/**
 * Most concrete photo capture component.
 * * Shows face guide
 * * Shows Capture/Retry/Cancel/Save buttons
 * * Shows errors as text
 * * Shows Cam switch if applicable
 */
export default class PhotoBooth extends React.PureComponent<{
  isLoading: boolean;
  onImageSubmit: (base64Img: string | null | undefined) => void;
  image: string | null | undefined;
  rootCss?: SerializedStyles;
  resources: Record<string, any>;
}, {
  img: string | null | undefined;
  isCaptureEnabled: boolean;
  isCapturing: boolean;
  mirror: boolean;

  error?: any;
  camId: string | number | null | undefined;
  camConnected: boolean;
  camOptions: any[];
}> {
  public static defaultProps = {
    image: null,
  };

  private webCam: any;
  private setRef = (webCam: any) => this.webCam = webCam;

  public state = {
    img:              null,
    isCaptureEnabled: false,
    isCapturing:      true,
    mirror:           false,
    error:            undefined,
    camId:            undefined,
    camConnected:     false,
    camOptions:       [],
  };


  constructor(props) {
    super(props);

    this.state.img         = props.image;
    this.state.isCapturing = isNilOrEmpty(props.image);
  }


  public componentDidMount() {
    // Get last camera and mirroring
    const camSettings    = getJsonCookie<any>(CAMERA_SETTINGS) || { lastCamId: '', mirror: {} };
    const previousCamera = camSettings.lastCamId || '';
    let mirror           = false;

    if (previousCamera) {
      this.setCamera(previousCamera);
      mirror = camSettings.mirror[previousCamera];
    }

    this.setState(prev => assoc('mirror', mirror, prev));

    if (canEnumerateCameras) {
      navigator.mediaDevices.enumerateDevices().then(
        devices => this.setCamOptions(
          devices
            .filter(deviceInfo => deviceInfo.kind === 'videoinput')
            .map((deviceInfo, index) => (
              {
                value: deviceInfo.deviceId,
                label: !deviceInfo.label || deviceInfo.label === '' ? `${getResource(this.props.resources, 'PHOTO_BOOTH_CAM')} ${index}` : deviceInfo.label,
              }
            ))),
      );
    }
  }


  public componentDidUpdate(prevProps, prevState) {
    // If the props image changed, update to it
    if (prevProps.image !== this.props.image) {
      this.setState(
        prev => R.mergeDeepRight(
          prev,
          {
            img:         this.props.image,
            isCapturing: isNilOrEmpty(this.props.image) && !this.props.isLoading,
          },
        ),
      );
    }

    // If we haven't changed anything about the cam config, then don't update the cookie
    if (prevState.camId !== this.state.camId || prevState.mirror !== this.state.mirror) {
      this.saveToCookie(this.state.camId, this.state.mirror);
    }
  }


  /*
   Event Handlers
   */

  private onCapture = () => {
    if (!this.webCam) {
      return;
    }

    this.webCam.getScreenshot();
  };


  private onReceiveImage = (capture: string | undefined) => this.setState(
    prev => R.mergeDeepRight(prev, { img: capture, isCapturing: false }),
  );


  private onRetake = () => this.setState(prev => assoc('isCapturing', true, prev));
  private onCancel = () => this.setState(prev => assoc('isCapturing', !prev.img, prev));
  private onSubmit = () => this.props.onImageSubmit(this.state.img);
  private onMirror = () => this.setState(prev => assoc('mirror', !prev.mirror, prev));


  /*
   Camera Handlers
   */


  private onChangeCamera = (event) => {
    this.setCamConnection(false);
    this.setCamError(undefined);
    this.setCamera(event.target.value);

    // Set mirroring if present in the cookie
    const camSettings  = getJsonCookie<any>(CAMERA_SETTINGS) || { lastCamId: '', mirror: {} };
    const camMirroring = camSettings.mirror[event.target.value];

    this.setState(state => assoc('mirror', !!camMirroring, state));
  };


  private onCamError = error => this.setCamError(error.message);


  private onCamConnect = () => {
    this.setCamConnection(true);
    this.setCamError(undefined);
  };


  private setCamera        = (camId: string) => this.setState(prev => assoc('camId', camId, prev));
  private setCamConnection = (connected: boolean) => this.setState(prev => assoc('camConnected', connected, prev));
  private setCamError      = (error: string | null | undefined) => this.setState(prev => assoc('error', error, prev));
  private setCamOptions    = (
    options: Array<{ value: string, label: string }>,
  ) => this.setState(prev => assoc('camOptions', options, prev));


  private saveToCookie = (camId, mirror = false) => {
    const camSettings = getJsonCookie<any>(CAMERA_SETTINGS) || { lastCamId: '', mirror: {} };

    camSettings.mirror[camId] = mirror;

    Cookies.set(CAMERA_SETTINGS, { lastCamId: camId, mirror: camSettings.mirror }, { secure: true });
  };


  public createCamOptions = (): React.ReactNode[] => (this.state.camOptions || [])
    .map((cam: { label: string, value: string }) => <Option value={cam.value} key={cam.value}>{cam.label}</Option>);


  public render() {
    const videoConstraints = {
      deviceId: canEnumerateCameras ? this.state.camId : undefined,
    };

    // Error
    if (this.state.error) {
      return <div
        css={css`
          width: ${PREVIEW_WIDTH};
          height: ${PREVIEW_HEIGHT};
          background-color: ${COOL_GREY};
        `}
      >
        <h4>WebCam Error</h4>
        <span>{this.state.error}</span>
      </div>;
    }

    const mainView = !this.state.isCapturing
      ? (
        this.props.isLoading
          ? <Loader
            rootCss={css`width: 100px;
              height: 100px;
              margin: 190px 0;`}
          />
          : <FadeImg src={this.state.img || ''} css={CameraLens} />
      )
      : <Column>
        {
          !this.state.camConnected
          && <Row
            justifyContent='center'
            alignItems='center'
            rootCss={CameraLens}
          >
            <FadeImg src={webCamImg} alt='Loading WebCam' css={CameraLens} />
          </Row>
        }
        <FadeDiv
          hidden={!this.state.camConnected}
          css={css`position: relative;`}
        >
          <FaceGuide />
            <WebCam
                  resources={this.props.resources}
                  ref={this.setRef}
                  onHasImage={this.onReceiveImage}
                  width={PREVIEW_WIDTH}
                  height={PREVIEW_HEIGHT}
                  screenshotWidth={INPUT_WIDTH}
                  screenshotHeight={INPUT_HEIGHT}
                  screenshotFormat={SCREENSHOT_FORMAT}
                  screenshotQuality={IMAGE_QUALITY}
                  videoConstraints={videoConstraints}
                  onUserMediaError={this.onCamError}
                  onConnectCam={this.onCamConnect}
                  mirror={this.state.mirror}
            />

        </FadeDiv>
        {
          this.state.camOptions
          && this.state.camOptions.length > 1
          && <FadeDiv>
            <StyledDiv>
              <Select
                id='cameraFacingSelect'
                name={getResource(this.props.resources, 'PHOTO_BOOTH_SELECT_WEBCAM')}
                css={MediumText}
                onChange={this.onChangeCamera}
                value={this.state.camId}
              >
                <option value="" disabled hidden>
                  {getResource(this.props.resources, 'PHOTO_BOOTH_SELECT_WEBCAM_PLACEHOLDER')}
                </option>
                {this.createCamOptions()}
              </Select>
            </StyledDiv>
          </FadeDiv>
        }
      </Column>;

    return (
      <Column alignItems='center' rootCss={this.props.rootCss}>
        { // Camera or Image
          mainView
        }
        <Row rootCss={css`align-items: center;`}>
          {
            this.state.isCapturing &&
              <Row title={getResource(this.props.resources, 'PHOTO_BOOTH_FLIP_CAMERA_FRAME_HORIZONTALLY')}>
                <input
                  id='mirror-capture'
                  type='checkbox'
                  checked={this.state.mirror}
                  title={getResource(this.props.resources, 'PHOTO_BOOTH_FLIP_CAMERA_IMAGE_HORIZONTALLY')}
                  onChange={this.onMirror}
                />
                <label
                  htmlFor='mirror-capture' css={css`
                  ${MediumText};
                  padding: 0 10px;
                  ${UNSELECTABLE_TEXT};
                `}
                >{getResource(this.props.resources, 'PHOTO_BOOTH_REVERSE_CAMERA')}</label>
              </Row>

          }
          { // Cancel Button
            this.state.img !== null
            && this.state.img !== ''
            && this.state.isCapturing
            && <ButtonNegative
              css={css`min-width: 130px;`}
              title={getResource(this.props.resources, 'PHOTO_BOOTH_CANCEL_RETAKE')}
              onClick={this.onCancel}
            >{getResource(this.props.resources, 'PHOTO_BOOTH_CANCEL')}</ButtonNegative>
          }
          { // Capture Button
            this.state.isCapturing
            && <ButtonPositive
              css={css`min-width: 130px;`}
              title={getResource(this.props.resources, 'PHOTO_BOOTH_TAKE_NEW_PICTURE')}
              onClick={this.onCapture}
              disabled={!this.state.camConnected}
            >{getResource(this.props.resources, 'PHOTO_BOOTH_CAPTURE')}</ButtonPositive>
          }
          { // Retake Button
            this.state.img !== null
            && this.state.img !== ''
            && !this.state.isCapturing
            && <ButtonNeutral
              title={getResource(this.props.resources, 'PHOTO_BOOTH_TAKE_PICTURE')}
              onClick={this.onRetake}
              css={css`min-width: 130px;`}
            >{getResource(this.props.resources, 'PHOTO_BOOTH_RETAKE')}</ButtonNeutral>
          }
          { // Save Button
            this.state.img !== null
            && this.state.img !== ''
            && !this.state.isCapturing
            && <ButtonPositive
              title={getResource(this.props.resources, 'PHOTO_BOOTH_SAVE_PICTURE')}
              onClick={this.onSubmit}
              css={css`min-width: 130px;`}
            >{getResource(this.props.resources, 'PHOTO_BOOTH_SAVE')}</ButtonPositive>
          }
        </Row>
      </Column>
    );
  }
}
